/**
 * 错误处理工具函数
 * 提供统一的错误处理和用户友好的错误提示
 */

import i18next from 'i18next';
import { BaseResponse } from '@/services';
import { isSmsErrorCode, getSmsErrorMessage } from '@/utils/constants/smsErrorCodes';

// 错误类型枚举
export enum ErrorType {
  NETWORK = 'NETWORK',
  BUSINESS = 'BUSINESS',
  VALIDATION = 'VALIDATION',
  UNKNOWN = 'UNKNOWN',
}

// 错误信息接口
export interface ErrorInfo {
  type: ErrorType;
  message: string;
  originalError?: any;
  code?: string | number;
}

/**
 * 解析API响应错误
 * @param response API响应
 * @returns 错误信息
 */
export const parseApiError = (response: BaseResponse<any>): ErrorInfo => {
  if (response.code === 0) {
    return {
      type: ErrorType.UNKNOWN,
      message: i18next.t('error.dataAbnormal', '操作成功但数据异常'),
    };
  }

  // 根据错误码分类
  let type = ErrorType.BUSINESS;
  let message = response.message || i18next.t('error.operationFailed', '操作失败');

  // 可以根据具体的错误码进行更细致的分类
  if (response.code === 401) {
    type = ErrorType.VALIDATION;
    message = i18next.t('error.authFailed', '身份验证失败，请重新登录');
  } else if (response.code === 403) {
    type = ErrorType.VALIDATION;
    message = i18next.t('error.permissionDenied', '权限不足，无法执行此操作');
  } else if (response.code === 429) {
    type = ErrorType.BUSINESS;
    message = i18next.t('error.rateLimited', '请求过于频繁，请稍后再试');
  } else if (isSmsErrorCode(response.code)) {
    // 短信相关错误码处理
    type = ErrorType.BUSINESS;
    message = getSmsErrorMessage(response.code);
  }

  return {
    type,
    message,
    code: response.code,
  };
};

/**
 * 解析异常错误
 * @param error 异常对象
 * @returns 错误信息
 */
export const parseExceptionError = (error: any): ErrorInfo => {
  if (!error) {
    return {
      type: ErrorType.UNKNOWN,
      message: i18next.t('error.unknown', '未知错误'),
    };
  }

  // 网络错误
  if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
    return {
      type: ErrorType.NETWORK,
      message: i18next.t('error.networkFailed', '网络连接失败，请检查网络设置'),
      originalError: error,
    };
  }

  // 超时错误
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return {
      type: ErrorType.NETWORK,
      message: i18next.t('error.requestTimeout', '请求超时，请稍后重试'),
      originalError: error,
    };
  }

  // 业务错误（从request.ts抛出的错误）
  if (error.message?.includes('Request failed with business error')) {
    return {
      type: ErrorType.BUSINESS,
      message: i18next.t('error.serverError', '服务器处理失败，请稍后重试'),
      originalError: error,
    };
  }

  // 其他错误
  return {
    type: ErrorType.UNKNOWN,
    message: error.message || i18next.t('error.genericError', '操作失败，请稍后重试'),
    originalError: error,
  };
};

/**
 * 统一的错误处理函数
 * @param error 错误对象（可能是API响应或异常）
 * @param context 错误上下文（用于日志记录）
 * @returns 错误信息
 */
export const handleError = (error: any, context?: string): ErrorInfo => {
  let errorInfo: ErrorInfo;

  // 判断是API响应错误还是异常错误
  if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
    // API响应错误
    errorInfo = parseApiError(error);
  } else {
    // 异常错误
    errorInfo = parseExceptionError(error);
  }

  // 记录错误日志
  const logMessage = context ? `[${context}] ${errorInfo.message}` : errorInfo.message;

  console.error(logMessage, {
    type: errorInfo.type,
    code: errorInfo.code,
    originalError: errorInfo.originalError || error,
  });

  return errorInfo;
};

/**
 * 短信相关的错误处理
 * @param error 错误对象
 * @param context 错误上下文（如：'发送验证码'、'短信登录'）
 * @returns 错误信息
 */
export const handleSmsError = (error: any, context: string = '短信服务'): ErrorInfo => {
  const errorInfo = handleError(error, context);

  // 针对短信服务的特殊错误处理
  if (errorInfo.type === ErrorType.BUSINESS && typeof errorInfo.code === 'number') {
    if (isSmsErrorCode(errorInfo.code)) {
      errorInfo.message = getSmsErrorMessage(errorInfo.code);
    }
  }

  return errorInfo;
};

/**
 * 生成任务相关的错误处理
 * @param error 错误对象
 * @param taskType 任务类型（如：'图片生成'、'视频生成'）
 * @returns 错误信息
 */
export const handleGenerationError = (error: any, taskType: string): ErrorInfo => {
  const errorInfo = handleError(error, `${taskType}任务`);

  // 针对生成任务的特殊错误处理
  if (errorInfo.type === ErrorType.BUSINESS) {
    // 可以根据具体的业务错误码提供更友好的提示
    if (errorInfo.code === 'INSUFFICIENT_CREDITS') {
      errorInfo.message = i18next.t('error.insufficientCredits', '积分不足，无法创建生成任务');
    } else if (errorInfo.code === 'MODEL_UNAVAILABLE') {
      errorInfo.message = i18next.t('error.modelUnavailable', '所选模型暂时不可用，请选择其他模型');
    } else if (errorInfo.code === 'CONTENT_VIOLATION') {
      errorInfo.message = i18next.t('error.contentViolation', '内容违规，请修改提示词后重试');
    } else {
      errorInfo.message = i18next.t('error.generationFailed', '{{taskType}}失败，请稍后重试', { taskType });
    }
  }

  return errorInfo;
};

/**
 * 获取用户友好的错误提示
 * @param errorInfo 错误信息
 * @returns 用户友好的错误提示
 */
export const getUserFriendlyMessage = (errorInfo: ErrorInfo): string => {
  switch (errorInfo.type) {
    case ErrorType.NETWORK:
      return i18next.t('error.networkAbnormal', '网络连接异常，请检查网络后重试');
    case ErrorType.VALIDATION:
      return errorInfo.message;
    case ErrorType.BUSINESS:
      return errorInfo.message;
    case ErrorType.UNKNOWN:
    default:
      return i18next.t('error.genericError', '操作失败，请稍后重试');
  }
};

/**
 * 检查是否为可重试的错误
 * @param errorInfo 错误信息
 * @returns 是否可重试
 */
export const isRetryableError = (errorInfo: ErrorInfo): boolean => {
  return (
    errorInfo.type === ErrorType.NETWORK ||
    (errorInfo.type === ErrorType.BUSINESS && errorInfo.code !== 'CONTENT_VIOLATION')
  );
};
